/**
 * Stable per-browser session id for attendee-facing AI metering (Phase 2K).
 *
 * Signed-out visitors have no `auth.uid()`, so the daily AI search quota is
 * keyed on this id instead. It is a random opaque value: no PII, no
 * fingerprinting, no cross-site value. It is a metering aid layered on top of
 * the proxy's IP rate limit, never an authentication or authorization signal.
 */

const STORAGE_KEY = 'ts.ai.session-id';

let cached: string | null = null;

const randomId = (): string => {
  try {
    return crypto.randomUUID();
  } catch {
    return `s-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
  }
};

export function getAISessionId(): string {
  if (cached) return cached;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      cached = stored;
      return stored;
    }
    const created = randomId();
    localStorage.setItem(STORAGE_KEY, created);
    cached = created;
    return created;
  } catch {
    // Private mode / storage disabled: fall back to an in-memory id.
    cached = cached ?? randomId();
    return cached;
  }
}

/** Test helper — clears the memoised id. */
export function resetAISessionId(): void {
  cached = null;
}
